import { twMerge } from 'tailwind-merge';
import { SortUpSolidGlyph, SortDownSolidGlyph } from '@acast-tech/decibel-glyphs';
import { headerTextStyle } from './TableHeader';
import type { SortableHeaderProps } from '@/types';

/**
 * This component is used to display a sortable header.
 * @param id - The field to sort by.
 * @param children - The children to display.
 * @param align - The alignment of the header.
 * @param className - The class name to apply to the header.
 * @param sortField - The sort field.
 * @param onSort - The function to call when the header is sorted.
 * @returns The sortable header component.
 */
const SortableHeader = ({
  id,
  children,
  align = 'left',
  className,
  sortField,
  onSort,
}: SortableHeaderProps) => {
  const isActive = sortField?.key === id;
  const direction = isActive && sortField?.direction;

  const buttonAlignment = align === 'right' ? 'justify-end' : 'justify-start';
  const alignStyles = {
    left: 'text-left',
    right: 'text-right',
  };

  return (
    <th className={twMerge('py-3', alignStyles[align], headerTextStyle, className)}>
      <button
        onClick={() => onSort(id)}
        className={twMerge(
          'flex items-center w-full gap-1 hover:text-gray-900 transition-colors duration-300',
          buttonAlignment,
          isActive && 'text-textDarkGrey'
        )}
      >
        <span className={alignStyles[align]}>{children}</span>

        <div className="ml-1 shrink-0">
          <SortUpSolidGlyph
            className={twMerge(
              'w-2.5 fill-textMedGrey translate-y-2 opacity-25',
              direction === 'asc' && 'opacity-100'
            )}
          />
          <SortDownSolidGlyph
            className={twMerge(
              'w-2.5 fill-textMedGrey -translate-y-2 opacity-25',
              direction === 'desc' && 'opacity-100'
            )}
          />
        </div>
      </button>
    </th>
  );
};

export default SortableHeader;
